/**
 * Configuration for the Register Tables.
 * Updated for compatibility with PrimeNG 21 Table modules.
 */

export interface ColumnConfig {
  title: string;
  field?: string;
  type?: 'text' | 'tag' | 'action' | 'truncated' | 'date';
  sortable?: boolean; // Added for PrimeNG [pSortableColumn]
  filterType?: 'text' | 'select' | 'multiselect' | 'date' | 'none'; // Filter type for the column
  filterOptions?: { label: string; value: any }[]; // Options for select filter
  filterPlaceholder?: string; // Placeholder text for filter input
  truncateAt?: number; // Max chars before truncation (only for type 'truncated')
}

export interface TableConfiguration {
  title: string;
  columns: ColumnConfig[];
  data: any[];
}

export const TABLE_CONFIG: Record<string, TableConfiguration> = {
  elencoFornitori: {
    title: 'Fornitori Attivi',
    columns: [
      {
        title: 'ID',
        field: 'id',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ID',
      },
      {
        title: 'Ragione Sociale',
        field: 'supplierName',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ragione sociale',
      },
      {
        title: 'Partita IVA',
        field: 'vatNumber',
        filterType: 'text',
        filterPlaceholder: 'Cerca P.IVA',
      },
      {
        title: 'Buyer',
        field: 'contactPerson',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca buyer',
      },
      {
        title: 'Stato',
        field: 'status',
        type: 'tag',
        filterType: 'select',
        filterPlaceholder: 'Filtra stato',
        filterOptions: [
          { label: 'Bozza', value: 'DRAFT' },
          { label: 'Attivo', value: 'ACTIVE' },
          { label: 'Inattivo', value: 'INACTIVE' },
          { label: 'Cessato', value: 'CEASED' },
        ],
      },
      {
        title: 'Data Registrazione',
        field: 'createdAt',
        sortable: true,
        filterType: 'date',
        filterPlaceholder: 'Filtra data',
      },
      { title: 'Azioni', type: 'action', filterType: 'none' },
    ],
    data: [
      {
        id: 1,
        ragione: 'Alpha Srl',
        iva: 'IT1234567',
        stato: 'ATTIVO',
        responsabile: 'Mario Rossi',
        data: '2024-02-10', // Recommended ISO format for better sorting in p-table
      },
      {
        id: 2,
        ragione: 'Beta Srl',
        iva: 'IT9876543',
        stato: 'BLOCCATO',
        responsabile: 'Luca Verdi',
        data: '2024-01-22',
      },
    ],
  },

  elencoFiduciari: {
    title: 'Fiduciari Registrati',
    columns: [
      {
        title: 'ID',
        field: 'id',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ID',
      },
      {
        title: 'Codice SOL',
        field: 'solCode',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca Codice SOL',
      },
      {
        title: 'Categoria',
        field: 'type',
        type: 'tag',
        sortable: true,
        filterType: 'multiselect',
        filterPlaceholder: 'Filtra categoria',
        filterOptions: [
          { label: 'Perito Auto', value: 'AUTO_EXPERT' },
          { label: 'Perito RE', value: 'RE_EXPERT' },
          { label: 'Medico Legale', value: 'DOCTOR' },
          { label: 'Legale', value: 'LAWYER' },
          { label: 'Investigatore', value: 'INVESTIGATOR' },
          { label: 'Carrozzeria', value: 'BODYSHOP' },
          { label: 'Carrozzeria Network', value: 'NK_BODYSHOP' },
          { label: 'Provider Grandine', value: 'HAIL_PROVIDER' },
          { label: 'Network Carrozzerie', value: 'NETWORK' },
        ],
      },
      {
        title: 'Ragione Sociale / Nominativo',
        field: 'supplierName',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ragione sociale o nominativo',
      },
      {
        title: 'Codice Identificativo',
        field: 'identificationCode',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca P.IVA/CF',
      },
      {
        title: 'Stato',
        field: 'professionalStatus',
        type: 'tag',
        sortable: true,
        filterType: 'multiselect',
        filterPlaceholder: 'Filtra stato',
        filterOptions: [
          { label: 'Attivo', value: 'ACTIVE' },
          { label: 'In Lavorazione', value: 'IN_PROGRESS' },
          { label: 'In Attesa Risposta', value: 'AWAITING_RESPONSE' },
          { label: 'Sospeso', value: 'SUSPENDED' },
          { label: 'Cessato', value: 'CEASED' },
        ],
      },
      {
        title: 'Data Creazione',
        field: 'createdAt',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Filtra data',
      },
      { title: 'Azioni', type: 'action', filterType: 'none' },
    ],
    data: [],
  },

  elencoResponsabili: {
    title: 'Responsabili',
    columns: [
      {
        title: 'ID Responsabile',
        field: 'id',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ID',
      },
      {
        title: 'Ragione Sociale',
        field: 'businessName',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ragione sociale',
      },
      {
        title: 'Sede',
        field: 'headquarters',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca sede',
      },
      {
        title: 'Email',
        field: 'email',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca email',
      },
      {
        title: 'Stato',
        field: 'status',
        type: 'tag',
        sortable: true,
        filterType: 'select',
        filterPlaceholder: 'Filtra stato',
        filterOptions: [
          { label: 'Attivo', value: 'ACTIVE' },
          { label: 'Bozza', value: 'DRAFT' },
          { label: 'Inattivo', value: 'INACTIVE' },
        ],
      },
      {
        title: 'Data Creazione',
        field: 'createdAt',
        sortable: true,
        filterType: 'date',
        filterPlaceholder: 'Filtra data',
      },
      { title: 'Azioni', type: 'action', filterType: 'none' },
    ],
    data: [],
  },

  elencoSubResponsabili: {
    title: 'Sub-Responsabili',
    columns: [
      {
        title: 'ID Sub-Responsabile',
        field: 'id',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ID',
      },
      {
        title: 'Ragione Sociale',
        field: 'businessName',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ragione sociale',
      },
      {
        title: 'Sede',
        field: 'headquarters',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca sede',
      },
      {
        title: 'Email',
        field: 'email',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca email',
      },
      {
        title: 'Stato',
        field: 'status',
        type: 'tag',
        sortable: true,
        filterType: 'select',
        filterPlaceholder: 'Filtra stato',
        filterOptions: [
          { label: 'Attivo', value: 'ACTIVE' },
          { label: 'Bozza', value: 'DRAFT' },
          { label: 'Inattivo', value: 'INACTIVE' },
        ],
      },
      {
        title: 'Data Creazione',
        field: 'createdAt',
        sortable: true,
        filterType: 'date',
        filterPlaceholder: 'Filtra data',
      },
      { title: 'Azioni', type: 'action', filterType: 'none' },
    ],
    data: [],
  },

  subresponsabili: {
    title: 'Sub-Responsabili',
    columns: [
      { title: 'ID', field: 'id' },
      { title: 'Nome', field: 'nome' },
      { title: 'Cognome', field: 'cognome' },
      { title: 'Area', field: 'area' },
      { title: 'Stato', field: 'stato', type: 'tag' },
    ],
    data: [{ id: 10, nome: 'Chiara', cognome: 'Gialli', area: 'Supporto', stato: 'ATTIVO' }],
  },

  registroFiduciari: {
    title: 'Registro Nuovi Fiduciari',
    columns: [
      {
        title: 'ID',
        field: 'id',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ID',
      },
      {
        title: 'Ragione Sociale',
        field: 'companyName',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ragione sociale',
      },
      {
        title: 'Partita IVA',
        field: 'vatNumber',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca partita IVA',
      },
      {
        title: 'Categoria',
        field: 'categoryDisplay',
        type: 'tag',
        sortable: true,
        filterType: 'select',
        filterPlaceholder: 'Filtra categoria',
        filterOptions: [
          { label: 'Perito Auto', value: 'PERITO AUTO' },
          { label: 'Perito RE', value: 'PERITO RE' },
          { label: 'Medico Legale', value: 'MEDICO' },
          { label: 'Legale', value: 'LEGALE' },
          { label: 'Investigatore', value: 'INVESTIGATORE' },
          { label: 'Carrozzeria', value: 'CARROZZERIA' },
          { label: 'Provider Grandine', value: 'PROVIDER GRANDINE' },
          { label: 'Network Carrozzerie', value: 'NETWORK CARROZZERIE' },
        ],
      },
      {
        title: 'Stato',
        field: 'statusDisplay',
        type: 'tag',
        sortable: true,
        filterType: 'select',
        filterPlaceholder: 'Filtra stato',
        filterOptions: [
          { label: 'In Lavorazione', value: 'In Lavorazione' },
          { label: 'In Attesa Risposta', value: 'In Attesa Risposta' },
        ],
      },
      {
        title: 'Data Registrazione',
        field: 'registrationDate',
        sortable: true,
        filterType: 'date',
        filterPlaceholder: 'Filtra data',
      },
      {
        title: 'Dettaglio',
        type: 'action',
        filterType: 'none',
      },
    ],
    data: [
      {
        id: '50001',
        companyName: 'Studio Legale Associato Rossi & Partners',
        vatNumber: '12345678901',
        category: 'LAWYER',
        categoryDisplay: 'LAWYER',
        status: 'IN_LAVORAZIONE',
        statusDisplay: 'IN LAVORAZIONE',
        registrationDate: '08/12/2025',
      },
      {
        id: '60001',
        companyName: 'Autocarrozzeria Milano Centro S.r.l.',
        vatNumber: '23456789012',
        category: 'BODYSHOP',
        categoryDisplay: 'BODYSHOP',
        status: 'IN_ATTESA_DI_RISPOSTA',
        statusDisplay: 'IN ATTESA DI RISPOSTA',
        registrationDate: '05/12/2025',
      },
      {
        id: '10001',
        companyName: 'Perito Assicurativo Giovanni Bianchi',
        vatNumber: '34567890123',
        category: 'AUTO_EXPERT',
        categoryDisplay: 'PERITO AUTO',
        status: 'IN_LAVORAZIONE',
        statusDisplay: 'IN LAVORAZIONE',
        registrationDate: '02/12/2025',
      },
      {
        id: '40001',
        companyName: 'Dr.ssa Laura Verdi - Medicina del Lavoro',
        vatNumber: '45678901234',
        category: 'MEDICO',
        categoryDisplay: 'MEDICO',
        status: 'IN_ATTESA_DI_RISPOSTA',
        statusDisplay: 'IN ATTESA DI RISPOSTA',
        registrationDate: '28/11/2025',
      },
      {
        id: '30001',
        companyName: 'Investigazioni Professionali Roma',
        vatNumber: '56789012345',
        category: 'INVESTIGATOR',
        categoryDisplay: 'INVESTIGATOR',
        status: 'IN_LAVORAZIONE',
        statusDisplay: 'IN LAVORAZIONE',
        registrationDate: '25/11/2025',
      },
      {
        id: '60002',
        companyName: 'Carrozzeria Specializzata Torino',
        vatNumber: '67890123456',
        category: 'CARROZZERIA',
        categoryDisplay: 'CARROZZERIA',
        status: 'IN_ATTESA_DI_RISPOSTA',
        statusDisplay: 'IN ATTESA DI RISPOSTA',
        registrationDate: '20/11/2025',
      },
      {
        id: '20001',
        companyName: 'Arch. Perito Immobiliare Andrea Russo',
        vatNumber: '78901234567',
        category: 'RE_EXPERT',
        categoryDisplay: 'PERITO RE',
        status: 'IN_LAVORAZIONE',
        statusDisplay: 'IN LAVORAZIONE',
        registrationDate: '15/11/2025',
      },
    ],
  },

  // Allineato a Template Documentali (document-template-list) - solo lettura,
  // niente Stato: gli elementi mostrati sono già ACTIVE. La colonna Azioni
  // ospita il pulsante "Seleziona" emesso da registry-table.
  elencoTemplateContratti: {
    title: 'Template Contratti',
    columns: [
      { title: 'Nome Template', field: 'name', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
      { title: 'Versione', field: 'versionLabel', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca versione' },
      { title: 'Ultima Modifica', field: 'lastModified', type: 'text', sortable: true, filterType: 'date' },
      { title: 'Azioni', type: 'action', filterType: 'none' },
    ],
    data: [],
  },

  elencoTemplateAttiNomina: {
    title: 'Template Atti di Nomina',
    columns: [
      { title: 'Nome Template', field: 'name', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
      { title: 'Versione', field: 'versionLabel', type: 'text', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca versione' },
      { title: 'Ultima Modifica', field: 'lastModified', type: 'text', sortable: true, filterType: 'date' },
      { title: 'Azioni', type: 'action', filterType: 'none' },
    ],
    data: [],
  },

  elencoBlocchiDiTesto: {
    title: 'Blocchi di Testo',
    columns: [
      {
        title: 'ID',
        field: 'id',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca ID',
      },
      {
        title: 'Nome Blocco',
        field: 'name',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca nome blocco',
      },
      {
        title: 'Entità Principale',
        field: 'rootTableDisplay',
        sortable: true,
        filterType: 'text',
        filterPlaceholder: 'Cerca entità',
      },
      {
        title: 'Ripetibile',
        field: 'isRepeatableDisplay',
        sortable: true,
        filterType: 'select',
        filterPlaceholder: 'Filtra ripetibilità',
        filterOptions: [
          { label: 'Sì', value: 'Sì' },
          { label: 'No', value: 'No' },
        ],
      },
      {
        title: 'Data Creazione',
        field: 'createdAt',
        sortable: true,
        filterType: 'date',
        filterPlaceholder: 'Filtra data',
      },
      { title: 'Azioni', type: 'action', filterType: 'none' },
    ],
    data: [],
  },
};
